<script setup lang="ts">
import ProjectSummaryCard from '@/components/projects/ProjectSummaryCard.vue';
import { Button } from '@/components/ui/button';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import { useProjectQuickCreate } from '@/composables/projects/useProjectQuickCreate';
import { BriefcaseBusiness, SquareGanttChart } from 'lucide-vue-next';
import type { Project } from '~/types/models';
import type { ClientProjectCardStats, ClientProjectLinkItem } from './types';

interface Props {
    relatedProjects: Project[];
    projectLinkItems: ClientProjectLinkItem[];
    projectStatsById: Record<string, ClientProjectCardStats | undefined>;
    canMutate: boolean;
    tenantSlug: string;
    linkedClientId?: string;
}

const props = withDefaults(defineProps<Props>(), {
    linkedClientId: '',
});
const { tenantPath } = useTenantPath();
const { openProjectQuickCreate } = useProjectQuickCreate();

const emit = defineEmits<{
    (e: 'edit', project: Project): void,
    (e: 'notice', project: Project): void,
    (e: 'publish', project: Project): void,
    (e: 'archive', project: Project): void,
    (e: 'delete', project: Project): void,
}>();

</script>

<template>
    <SectionSurface eyebrow="Linked work" title="Projects" description="Delivery work already connected to this client." :icon="SquareGanttChart" density="compact" presentation="editorial">
        <template #actions>
            <div class="flex items-center gap-2">
                <div class="app-section-meta hidden sm:inline-flex">{{ relatedProjects.length }} linked</div>
                <Button v-if="canMutate" variant="outline" size="sm" class="rounded-full" @click="openProjectQuickCreate(linkedClientId)">
                    <BriefcaseBusiness class="mr-2 size-4" />
                    Create project
                </Button>
            </div>
        </template>

        <div v-if="relatedProjects.length === 0" class="empty-state-panel mt-4 min-h-24">
            No projects linked yet.
        </div>

        <GroupedEntityList v-else class="mt-4">
            <ProjectSummaryCard
                v-for="item in projectLinkItems"
                :key="item.project.id"
                :project="item.project"
                :href="item.href || undefined"
                :tenant-slug="tenantSlug"
                :show-client="false"
                :show-client-media="false"
                :can-mutate="canMutate"
                :stats="projectStatsById[item.project.id]"
                surface="list"
                @edit="emit('edit', $event)"
                @notice="emit('notice', $event)"
                @publish="emit('publish', $event)"
                @archive="emit('archive', $event)"
                @delete="emit('delete', $event)"
            />
        </GroupedEntityList>
    </SectionSurface>
</template>
